import { InfiniteData, useMutation, useQueryClient } from 'react-query';

import { api } from '@services';
import {
  VideosData,
  VideoCommentData,
  VideoCommentsResponse,
} from '@common/types/api';

import { useAuth } from '../useAuth';
import { selectUser } from '../useAuth/selectors';

type SortOrder = 'newest' | 'oldest';

export const useVideoCommentMutation = (
  videoId: number,
  hasModerationEnabled: boolean,
  sortOrder: SortOrder,
) => {
  const queryClient = useQueryClient();
  const user = useAuth(selectUser);
  const detailKey = ['video-detail', String(videoId)];
  const commentsKey = ['video-comments', String(videoId), sortOrder];

  return useMutation(
    (comment: string) => api.post(`/videos/${videoId}/comment`, { comment }),
    {
      onMutate: async (comment: string) => {
        await queryClient.cancelQueries(commentsKey);
        await queryClient.cancelQueries(detailKey);

        const previousComments =
          queryClient.getQueryData<InfiniteData<VideoCommentsResponse>>(
            commentsKey,
          );
        const previousDetail = queryClient.getQueryData<VideosData>(detailKey);

        if (previousDetail) {
          queryClient.setQueryData<VideosData>(detailKey, {
            ...previousDetail,
            comments_count: previousDetail.comments_count + 1,
          });
        }

        if (previousComments && previousComments.pages.length > 0) {
          const date = new Date().toISOString();

          const newComment = {
            id: Date.now(),
            video_id: videoId,
            user_id: user.id,
            comment,
            status: 1,
            created_at: date,
            updated_at: date,
            user: {
              data: {
                id: user.id,
                name: user.name,
                avatar: user.avatar,
              },
            },
          } as VideoCommentData;

          const newPages = [...previousComments.pages];

          if (sortOrder === 'newest') {
            newPages[0] = {
              ...newPages[0],
              data: [newComment, ...newPages[0].data],
            };
          } else {
            const lastIdx = newPages.length - 1;
            newPages[lastIdx] = {
              ...newPages[lastIdx],
              data: [...newPages[lastIdx].data, newComment],
            };
          }

          queryClient.setQueryData<InfiniteData<VideoCommentsResponse>>(
            commentsKey,
            { ...previousComments, pages: newPages },
          );
        }

        return { previousComments, previousDetail };
      },
      onError: (_, __, ctx) => {
        if (ctx?.previousComments) {
          queryClient.setQueryData(commentsKey, ctx.previousComments);
        }
        if (ctx?.previousDetail) {
          queryClient.setQueryData(detailKey, ctx.previousDetail);
        }
      },
      onSettled: () => {
        if (!hasModerationEnabled) {
          queryClient.invalidateQueries(commentsKey);
        }
        queryClient.invalidateQueries(detailKey);
      },
    },
  );
};
